// src/pages/Analytics.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  getUserProfileStats,
  getSessionsByDateRange,
  generateHeatmapData,
  getActiveUserLatestLocations,
} from '../utils/analytics';

// Chart.js 関連
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';

// Leaflet 関連
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

// Chart.js の登録
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  // ★ 追加：activeユーザー位置情報
  const [activeUserLocations, setActiveUserLocations] = useState([]);
  const [activeLocationsLoading, setActiveLocationsLoading] = useState(false);

  // 地域フィルタ（空文字なら全体）
  const [regionCode, setRegionCode] = useState('');

  // 日付範囲
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  });

  // ヒートマップ用ポイント
  const [heatmapPoints, setHeatmapPoints] = useState([]);

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const filterRegion = regionCode.trim() === '' ? null : regionCode.trim();

      // 1. ユーザー統計
      const userStats = await getUserProfileStats(filterRegion);
      setStats(userStats);

      // 2. 日付範囲のセッション
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      const recentSessions = await getSessionsByDateRange(startDate, endDate);
      setSessions(recentSessions);

      // 3. ヒートマップ
      const locations = await generateHeatmapData();
      setHeatmapPoints(locations);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange.start, dateRange.end, regionCode]);

  // ★ 追加：更新ボタン押下時点のactiveユーザー最新位置を取得
  const loadActiveUserLocations = useCallback(async () => {
    setActiveLocationsLoading(true);
    try {
      const locations = await getActiveUserLatestLocations();
      setActiveUserLocations(locations);
    } catch (err) {
      console.error('Error loading active user locations:', err);
    } finally {
      setActiveLocationsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateRangeChange = (field, value) => {
    setDateRange((prev) => ({ ...prev, [field]: value }));
  };

  const handleRefresh = () => {
    loadDashboardData();
  };

  const handleApplyRegion = () => {
    loadDashboardData();
  };

  // ================= グラフ用データ =================

  const totalUsers = stats?.totalUsers || 0;

  // ▼ 性別分布（円グラフ / 青系）
  const genderPieData = stats
    ? {
        labels: ['男性', '女性', 'その他'],
        datasets: [
          {
            data: [
              stats.genderStats.male || 0,
              stats.genderStats.female || 0,
              stats.genderStats.other || 0,
            ],
            backgroundColor: ['#4B8BF5', '#7FB3FF', '#A5B8D9'],
            borderColor: '#ffffff',
            borderWidth: 2,
          },
        ],
      }
    : null;

  // ▼ 年代分布（棒グラフ / 青系グラデーション）
  const ageOrder = [
    'under10',
    '10s',
    '20s',
    '30s',
    '40s',
    '50s',
    '60s',
    '70s',
    '80s',
  ];
  const ageLabelMap = {
    under10: '10歳未満',
    '10s': '10代',
    '20s': '20代',
    '30s': '30代',
    '40s': '40代',
    '50s': '50代',
    '60s': '60代',
    '70s': '70代',
    '80s': '80代',
  };

  const ageColors = [
    '#4B8BF5',
    '#7FB3FF',
    '#6EC6CA',
    '#A5B8D9',
    '#2C3E50',
    '#4B8BF5',
    '#7FB3FF',
    '#6EC6CA',
    '#A5B8D9',
  ];

  const ageBarData = stats
    ? (() => {
        const labels = [];
        const values = [];
        const colors = [];

        ageOrder.forEach((key, index) => {
          labels.push(ageLabelMap[key]);
          values.push(stats.ageStats[key] || 0);
          colors.push(ageColors[index % ageColors.length]);
        });

        return {
          labels,
          datasets: [
            {
              label: '人数',
              data: values,
              backgroundColor: colors,
            },
          ],
        };
      })()
    : null;

  // ▼ 利用形態（personal / business / education）
  const usageLabelMap = {
    personal: '個人利用',
    business: 'ビジネス',
    education: '教育利用',
  };

  const usageBarData = stats
    ? {
        labels: [
          usageLabelMap.personal,
          usageLabelMap.business,
          usageLabelMap.education,
        ],
        datasets: [
          {
            label: '人数',
            data: [
              stats.usageStats.personal || 0,
              stats.usageStats.business || 0,
              stats.usageStats.education || 0,
            ],
            backgroundColor: ['#4B8BF5', '#7FB3FF', '#6EC6CA'],
          },
        ],
      }
    : null;

  // ▼ 同行者（family / alone / friends / couple）
  const companionsLabelMap = {
    family: '家族',
    alone: '一人',
    friends: '友人',
    couple: 'カップル',
  };

  const companionsKeys = ['family', 'alone', 'friends', 'couple'];

  const companionsBarData = stats
    ? {
        labels: companionsKeys.map((key) => companionsLabelMap[key]),
        datasets: [
          {
            label: '人数',
            data: companionsKeys.map(
              (key) => stats.companionsStats[key] || 0
            ),
            backgroundColor: ['#4B8BF5', '#7FB3FF', '#6EC6CA', '#A5B8D9'],
          },
        ],
      }
    : null;

  // ================= レンダリング =================

  if (loading) {
    return (
      <div className="dashboard-loading">
        <h2>データを読み込み中...</h2>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <header className="dashboard-header">
        <div>
          <h1>観光ガイドアプリ 管理ダッシュボード</h1>
          <p className="region-caption">
            表示対象:{' '}
            {regionCode.trim() === ''
              ? '全体'
              : `地域コード "${regionCode.trim()}"`}
          </p>
        </div>
        <button onClick={handleRefresh} className="refresh-button">
          データを更新
        </button>
      </header>

      {/* 地域フィルタ */}
      <section className="region-filter">
        <h2>地域フィルタ</h2>
        <div className="region-filter-controls">
          <label>
            地域コード（空欄で全体）:
            <input
              type="text"
              value={regionCode}
              onChange={(e) => setRegionCode(e.target.value)}
              placeholder='例: "edajima"'
            />
          </label>
          <button onClick={handleApplyRegion}>地域を適用</button>
        </div>
      </section>

      {/* ユーザー統計（アンケート結果） */}
      {stats && (
        <section className="stats-section">
          <h2>ユーザー統計（アンケート結果）</h2>
          <div className="stats-grid">
            <div className="stat-card">
              <h3>総ユーザー数</h3>
              <div className="stat-value">{stats.totalUsers}</div>
            </div>

            <div className="stat-card">
              <h3>性別（数値）</h3>
              <div className="stat-breakdown">
                <div>男性: {stats.genderStats.male || 0}</div>
                <div>女性: {stats.genderStats.female || 0}</div>
                <div>その他: {stats.genderStats.other || 0}</div>
              </div>
            </div>

            <div className="stat-card">
              <h3>年代（数値）</h3>
              <div className="stat-breakdown">
                {ageOrder.map((ageKey) => (
                  <div key={ageKey}>
                    {ageLabelMap[ageKey]}: {stats.ageStats[ageKey] || 0}
                  </div>
                ))}
              </div>
            </div>

            <div className="stat-card">
              <h3>利用形態（数値）</h3>
              <div className="stat-breakdown">
                <div>個人利用: {stats.usageStats.personal || 0}</div>
                <div>ビジネス: {stats.usageStats.business || 0}</div>
                <div>教育利用: {stats.usageStats.education || 0}</div>
              </div>
            </div>

            <div className="stat-card">
              <h3>同行者（数値）</h3>
              <div className="stat-breakdown">
                <div>家族: {stats.companionsStats.family || 0}</div>
                <div>一人: {stats.companionsStats.alone || 0}</div>
                <div>友人: {stats.companionsStats.friends || 0}</div>
                <div>カップル: {stats.companionsStats.couple || 0}</div>
              </div>
            </div>
          </div>

          {/* グラフ群 */}
          {totalUsers > 0 && (
            <div className="charts-grid">
              <div className="chart-card">
                <h3>性別分布（円グラフ）</h3>
                {genderPieData && <Pie data={genderPieData} />}
              </div>

              <div className="chart-card">
                <h3>年代分布（棒グラフ）</h3>
                {ageBarData && (
                  <Bar
                    data={ageBarData}
                    options={{
                      responsive: true,
                      plugins: { legend: { display: false } },
                      scales: {
                        x: { title: { display: true, text: '年代' } },
                        y: {
                          title: { display: true, text: '人数' },
                          beginAtZero: true,
                        },
                      },
                    }}
                  />
                )}
              </div>

              <div className="chart-card">
                <h3>利用形態（棒グラフ）</h3>
                {usageBarData && (
                  <Bar
                    data={usageBarData}
                    options={{
                      responsive: true,
                      plugins: { legend: { display: false } },
                      scales: {
                        x: { title: { display: true, text: '用途' } },
                        y: {
                          title: { display: true, text: '人数' },
                          beginAtZero: true,
                        },
                      },
                    }}
                  />
                )}
              </div>

              <div className="chart-card">
                <h3>同行者（棒グラフ）</h3>
                {companionsBarData && (
                  <Bar
                    data={companionsBarData}
                    options={{
                      responsive: true,
                      plugins: { legend: { display: false } },
                      scales: {
                        x: { title: { display: true, text: '同行者' } },
                        y: {
                          title: { display: true, text: '人数' },
                          beginAtZero: true,
                        },
                      },
                    }}
                  />
                )}
              </div>
            </div>
          )}

          {totalUsers === 0 && <p>該当するユーザーがまだいません。</p>}
        </section>
      )}

      {/* セッション統計 */}
      <section className="sessions-section">
        <h2>セッション統計</h2>

        <div className="date-range-controls">
          <label>
            開始日:
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => handleDateRangeChange('start', e.target.value)}
            />
          </label>
          <label>
            終了日:
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => handleDateRangeChange('end', e.target.value)}
            />
          </label>
          <button onClick={handleRefresh}>期間更新</button>
        </div>

        <div className="sessions-stats">
          <div className="stat-card">
            <h3>期間内セッション数</h3>
            <div className="stat-value">{sessions.length}</div>
          </div>
          <div className="stat-card">
            <h3>完了セッション数</h3>
            <div className="stat-value">
              {sessions.filter((s) => s.status === 'completed').length}
            </div>
          </div>
        </div>
      </section>

      {/* ★ 追加：アクティブユーザー位置マップ */}
      <section className="active-users-map-section">
        <div className="section-header">
          <div>
            <h2>アクティブユーザー現在位置</h2>
            <p className="heatmap-caption">
              status が active のユーザーについて、更新ボタン押下時点の最新位置を表示します。
            </p>
          </div>
          <button
            onClick={loadActiveUserLocations}
            className="refresh-button"
            disabled={activeLocationsLoading}
          >
            {activeLocationsLoading ? '更新中...' : '現在位置を更新'}
          </button>
        </div>

        <div className="active-users-summary">
          表示中のアクティブユーザー数: {activeUserLocations.length}
        </div>

        <div className="heatmap-map-wrapper">
          <MapContainer
            center={[34.67756, 135.480345]}
            zoom={15}
            style={{ height: '400px', width: '100%' }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap contributors"
            />
            {activeUserLocations.map((user) => (
              <CircleMarker
                key={`${user.userId}-${user.sessionId}`}
                center={[user.lat, user.lng]}
                radius={2}
                pathOptions={{
                  color: '#ff4d4f',
                  fillColor: '#ff4d4f',
                  fillOpacity: 0.85,
                }}
              >
                <Popup>
                  <div>
                    <strong>ユーザーID:</strong> {user.userId.substring(0, 12)}...
                    <br />
                    <strong>セッションID:</strong>{' '}
                    {user.sessionId.substring(0, 12)}...
                    <br />
                    <strong>緯度:</strong> {user.lat}
                    <br />
                    <strong>経度:</strong> {user.lng}
                    <br />
                    <strong>取得時刻:</strong>{' '}
                    {user.timestampId
                      ? new Date(user.timestampId).toLocaleString()
                      : 'N/A'}
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
      </section>

      {/* ヒートマップ */}
      <section className="heatmap-section">
        <h2>移動ヒートマップ（簡易）</h2>
        <p className="heatmap-caption">
          全ユーザーの一部セッションから取得した位置情報の分布です。
        </p>
        <div className="heatmap-map-wrapper">
          <MapContainer
            center={[34.67756, 135.480345]}
            zoom={15}
            style={{ height: '400px', width: '100%' }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap contributors"
            />
            {heatmapPoints.map((p, idx) => (
              <CircleMarker
                key={idx}
                center={[p.lat, p.lng]}
                radius={4}
                pathOptions={{
                  color: 'rgba(75, 139, 245, 0.35)',
                  fillColor: 'rgba(75, 139, 245, 0.35)',
                  fillOpacity: 0.35,
                }}
              />
            ))}
          </MapContainer>
        </div>
      </section>

      {/* 最近のセッション一覧 */}
      <section className="recent-sessions">
        <h2>最近のセッション</h2>
        <div className="sessions-table">
          {sessions.slice(0, 10).map((session) => (
            <div
              key={`${session.userId}-${session.sessionId}`}
              className="session-row"
            >
              <div className="session-info">
                <strong>ユーザーID:</strong>{' '}
                {session.userId.substring(0, 12)}...
              </div>
              <div className="session-info">
                <strong>開始時刻:</strong>{' '}
                {session.startTime
                  ? new Date(
                      session.startTime.seconds * 1000
                    ).toLocaleString()
                  : 'N/A'}
              </div>
              <div className="session-info">
                <strong>ステータス:</strong>{' '}
                <span className={`status ${session.status}`}>
                  {session.status === 'completed' ? '完了' : 'アクティブ'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <style>{`
        .admin-dashboard {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
          font-family: Arial, sans-serif;
        }

        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 2px solid #eee;
        }

        .region-caption {
          margin-top: 8px;
          font-size: 13px;
          color: #666;
        }

        .refresh-button {
          background: #4B8BF5;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 5px;
          cursor: pointer;
        }

        .refresh-button:disabled {
          background: #9bbcf5;
          cursor: not-allowed;
        }

        .region-filter {
          margin-bottom: 20px;
        }

        .region-filter-controls {
          display: flex;
          gap: 15px;
          align-items: center;
          padding: 10px 15px;
          background: #f8f9fa;
          border-radius: 8px;
        }

        .region-filter-controls input {
          padding: 5px 8px;
          border-radius: 4px;
          border: 1px solid #ccc;
          min-width: 160px;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .stat-card {
          background: white;
          padding: 20px;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.08);
        }

        .stat-card h3 {
          margin: 0 0 10px 0;
          color: #333;
        }

        .stat-value {
          font-size: 2em;
          font-weight: bold;
          color: #4B8BF5;
        }

        .stat-breakdown {
          font-size: 14px;
          line-height: 1.6;
        }

        .charts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 20px;
          margin-top: 10px;
        }

        .chart-card {
          background: white;
          padding: 16px;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.08);
        }

        .chart-card h3 {
          margin: 0 0 10px 0;
          font-size: 15px;
        }

        .sessions-section {
          margin-top: 40px;
        }

        .date-range-controls {
          display: flex;
          gap: 15px;
          align-items: center;
          margin-bottom: 20px;
          padding: 15px;
          background: #f8f9fa;
          border-radius: 8px;
        }

        .date-range-controls input {
          padding: 5px;
          border: 1px solid #ddd;
          border-radius: 4px;
        }

        .sessions-stats {
          display: flex;
          gap: 20px;
          margin-bottom: 20px;
        }

        .active-users-map-section {
          margin-top: 40px;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 10px;
        }

        .active-users-summary {
          font-size: 14px;
          color: #333;
          margin-bottom: 10px;
          font-weight: bold;
        }

        .heatmap-section {
          margin-top: 40px;
        }

        .heatmap-caption {
          font-size: 13px;
          color: #666;
          margin-bottom: 10px;
        }

        .heatmap-map-wrapper {
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }

        .sessions-table {
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }

        .session-row {
          padding: 15px;
          border-bottom: 1px solid #eee;
          display: grid;
          grid-template-columns: 2fr 2fr 1fr;
          gap: 15px;
          align-items: center;
        }

        .session-row:last-child {
          border-bottom: none;
        }

        .session-info {
          font-size: 14px;
        }

        .status {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: bold;
        }

        .status.completed {
          background: #d4edda;
          color: #155724;
        }

        .status.active {
          background: #fff3cd;
          color: #856404;
        }

        .dashboard-loading {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 400px;
        }
      `}</style>
    </div>
  );
}

export default AdminDashboard;